import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';

/**
 * Contratos do funil MAPA e dos direitos de privacidade.
 *
 * Este é o arquivo de DADO PESSOAL do projeto. Três coisas aqui não são estilo,
 * são cerca:
 *
 * 1. **Nenhum DTO público carrega identificador de pessoa.** IGSID, texto da
 *    Direct, telefone completo, código de atribuição e seletor de privacidade
 *    não aparecem em nenhum schema deste arquivo. O que sai para a tela é o
 *    pseudônimo, os 4 últimos dígitos e estado. Todo objeto é `strictObject`,
 *    então uma chave a mais é recusada na borda em vez de vazar para o painel,
 *    para o log de acesso e para o corpo de erro que alguém copia no chat.
 * 2. **Consentimento não nasce de texto livre.** `whatsappConsent` é enum e o
 *    pedido de resgate exige código opaco, nunca uma frase interpretada.
 * 3. **Data é sempre ISO 8601 em UTC.** Prazo legal contado sobre string de
 *    formato solto é prazo que ninguém consegue defender depois.
 *
 * Cuidado ao mexer: o pseudônimo tem a MESMA forma que o painel aprovado já
 * desenha (`MapaConversationReferenceSchema` em `panel.ts`). Alargar aqui sem
 * alargar lá faz a conversa sumir da tela sem erro visível.
 */

// ---------------------------------------------------------------------------
// Vocabulário do funil
// ---------------------------------------------------------------------------

/**
 * Estágios do diagnóstico, na ordem em que a conversa anda.
 *
 * `human_handoff`, `expired` e `opted_out` são saídas, e não etapas: a
 * automação não volta delas sozinha.
 */
export const MAPA_STAGES = [
  'idle',
  'notice_pending',
  'question_company_type',
  'question_task',
  'question_frequency',
  'question_platforms',
  'question_whatsapp',
  'completed_without_whatsapp',
  'link_issued',
  'whatsapp_message_confirmed',
  'channels_linked',
  'human_handoff',
  'expired',
  'opted_out',
] as const;
/**
 * Etapa da conversa no funil, do ponto de vista da ESTEIRA.
 *
 * Nome distinto do `MapaStageSchema` de `panel.ts`, que descreve a etapa do
 * ponto de vista da TELA e tem outro conjunto de valores. Reaproveitar o nome
 * faria os dois se atropelarem no index dos contratos, e pior: faria parecer
 * que descrevem a mesma coisa.
 */
export const MapaConversationStageSchema = z.enum(MAPA_STAGES);
export type MapaConversationStage = z.infer<typeof MapaConversationStageSchema>;

/** Exatamente cinco perguntas. Nem quatro, nem seis, e sempre nesta ordem. */
export const MAPA_QUESTIONS = [
  'company_type',
  'time_consuming_task',
  'frequency',
  'platforms',
  'whatsapp_permission',
] as const;
export const MapaQuestionSchema = z.enum(MAPA_QUESTIONS);
export type MapaQuestion = z.infer<typeof MapaQuestionSchema>;

export const MapaFrequencySchema = z.enum([
  'daily',
  'weekly',
  'monthly',
  'sporadic',
  'other',
]);
export type MapaFrequency = z.infer<typeof MapaFrequencySchema>;

/**
 * Resposta já interpretada.
 *
 * A união é discriminada pela pergunta porque cada uma tem forma própria: sem
 * isso, um array de plataformas caberia no campo do tipo de empresa e a
 * validação passaria.
 *
 * As respostas 1 e 2 são texto da pessoa, ou seja, dado pessoal. Elas existem
 * neste contrato porque a correção de dados (Art. 18 da LGPD) precisa
 * trafegá-las de volta ao titular, e por isso NÃO aparecem em
 * `LeadConversationDto`, só no detalhe autenticado e no pacote de acesso.
 */
export const AnswerValueSchema = z.discriminatedUnion('question', [
  z.strictObject({
    question: z.literal('company_type'),
    value: z.string().trim().min(2).max(100),
  }),
  z.strictObject({
    question: z.literal('time_consuming_task'),
    value: z.string().trim().min(5).max(300),
  }),
  z.strictObject({
    question: z.literal('frequency'),
    value: MapaFrequencySchema,
  }),
  z.strictObject({
    question: z.literal('platforms'),
    value: z.array(z.string().trim().min(2).max(80)).min(1).max(10),
  }),
  z.strictObject({
    question: z.literal('whatsapp_permission'),
    value: z.boolean(),
  }),
]);
export type AnswerValue = z.infer<typeof AnswerValueSchema>;

/**
 * Referência pseudonimizada da conversa.
 *
 * Mesma forma do painel aprovado. O espaço é pequeno de propósito (65.536
 * valores): quem garante unicidade é o índice do banco, e a emissão tenta de
 * novo quando colide. Formato não é garantia de unicidade, e tratar como se
 * fosse produziria duas conversas com o mesmo rótulo na tela.
 */
export const MapaPseudonymSchema = z.string().regex(/^lead_[0-9a-f]{4}$/u);

export const RetentionClassSchema = z.enum([
  'incomplete',
  'completed_unqualified',
  'qualified',
]);
export type RetentionClass = z.infer<typeof RetentionClassSchema>;

export const LeadConversationSchema = z.strictObject({
  id: UuidV4Schema,
  pseudonym: MapaPseudonymSchema,
  stage: MapaConversationStageSchema,
  currentQuestion: MapaQuestionSchema.nullable(),
  startedAt: IsoUtcDateTimeSchema,
  lastInteractionAt: IsoUtcDateTimeSchema,
  /** Fim da janela móvel de 24 horas. Instante, e não "restam X horas". */
  windowEndsAt: IsoUtcDateTimeSchema,
  automationAllowed: z.boolean(),
  retentionClass: RetentionClassSchema,
  purgeAfter: IsoUtcDateTimeSchema,
  destination: z.enum(['none', 'whatsapp', 'human']),
  /** Trava otimista. Duas telas decidindo sobre a mesma conversa colidem. */
  revision: z.int().nonnegative(),
});
export type LeadConversationDto = z.infer<typeof LeadConversationSchema>;

export const WhatsappConsentStateSchema = z.enum([
  'not_asked',
  'granted',
  'revoked',
]);
export type WhatsappConsentState = z.infer<typeof WhatsappConsentStateSchema>;

export const OutboundMessageStateSchema = z.enum([
  'queued',
  'send_call_started',
  'sent',
  'uncertain',
  'blocked',
]);
export type OutboundMessageState = z.infer<typeof OutboundMessageStateSchema>;

export const LeadConversationDetailSchema = LeadConversationSchema.extend({
  answers: z.array(AnswerValueSchema),
  /** Ciência do aviso. NÃO é consentimento, e por isso é outro campo. */
  noticeAcknowledgedAt: IsoUtcDateTimeSchema.nullable(),
  whatsappConsent: WhatsappConsentStateSchema,
  /** Só os 4 últimos dígitos. O telefone inteiro nunca sai do banco cifrado. */
  phoneLast4: z
    .string()
    .regex(/^\d{4}$/u)
    .nullable(),
  outbound: z.array(
    z.strictObject({
      state: OutboundMessageStateSchema,
      createdAt: IsoUtcDateTimeSchema,
    }),
  ),
});
export type LeadConversationDetailDto = z.infer<
  typeof LeadConversationDetailSchema
>;

/**
 * Eventos do funil. Cada um acontece no máximo uma vez por conversa: o webhook
 * da Meta repete e entrega fora de ordem, e um funil que conta duas vezes o
 * mesmo evento reporta um resultado que não existiu.
 */
export const FUNNEL_EVENT_KINDS = [
  'mapa_started',
  'notice_continued',
  'diagnostic_completed',
  'whatsapp_consent_granted',
  'whatsapp_link_issued',
  'whatsapp_redirect_clicked',
  'whatsapp_message_confirmed',
  'channels_linked',
] as const;
export const FunnelEventKindSchema = z.enum(FUNNEL_EVENT_KINDS);
export type FunnelEventKind = z.infer<typeof FunnelEventKindSchema>;

export const HandoffReasonSchema = z.enum([
  'invalid_answers',
  'sensitive_content',
  'subject_request',
  'owner_decision',
]);
export type HandoffReason = z.infer<typeof HandoffReasonSchema>;

// ---------------------------------------------------------------------------
// Resgate manual do código
// ---------------------------------------------------------------------------

/**
 * 22 caracteres base64url sem preenchimento, ou seja, 16 bytes de entropia.
 * Aceitar tamanho variável abriria espaço para código curto gerado à mão.
 */
export const AttributionCodeSchema = z.string().regex(/^[A-Za-z0-9_-]{22}$/u);

/** E.164 puro. Máscara e espaço são recusados, e não limpos. */
export const PhoneE164Schema = z.string().regex(/^\+[1-9]\d{7,14}$/u);

export const RedeemWhatsappCodeRequestSchema = z.strictObject({
  code: AttributionCodeSchema,
  whatsappPhoneE164: PhoneE164Schema,
});
export type RedeemWhatsappCodeRequest = z.infer<
  typeof RedeemWhatsappCodeRequestSchema
>;

export const WhatsappLinkSchema = z.strictObject({
  conversationId: UuidV4Schema,
  state: z.literal('channels_linked'),
  phoneLast4: z.string().regex(/^\d{4}$/u),
  linkedAt: IsoUtcDateTimeSchema,
});
export type WhatsappLinkDto = z.infer<typeof WhatsappLinkSchema>;

// ---------------------------------------------------------------------------
// Privacidade: aviso
// ---------------------------------------------------------------------------

export const CreatePrivacyNoticeRequestSchema = z.strictObject({
  version: z.string().trim().min(1).max(40),
  shortText: z.string().trim().min(1).max(1_000),
  fullUrl: z.url().refine((value) => value.startsWith('https://'), {
    message: 'o aviso completo precisa estar em https',
  }),
  /**
   * Referência da revisão jurídica. É obrigatória no schema porque a ativação
   * sem revisão é exatamente o erro que ninguém percebe: o piloto roda, coleta
   * dado e só descobre o problema quando alguém reclama.
   */
  legalReviewReference: z.string().trim().min(1).max(200),
  reviewedAt: IsoUtcDateTimeSchema,
  legalBasisSummary: z.string().trim().min(1).max(2_000),
  retentionRules: z.array(z.string().trim().min(1).max(300)).min(1).max(20),
});
export type CreatePrivacyNoticeRequest = z.infer<
  typeof CreatePrivacyNoticeRequestSchema
>;

export const PrivacyNoticeSchema = CreatePrivacyNoticeRequestSchema.extend({
  id: UuidV4Schema,
  state: z.enum(['draft', 'active', 'retired']),
  activatedAt: IsoUtcDateTimeSchema.nullable(),
});
export type PrivacyNoticeDto = z.infer<typeof PrivacyNoticeSchema>;

// ---------------------------------------------------------------------------
// Privacidade: pedido do titular
// ---------------------------------------------------------------------------

export const PrivacyRightSchema = z.enum([
  'access',
  'correction',
  'opposition',
  'consent_revocation',
  'erasure',
]);
export type PrivacyRight = z.infer<typeof PrivacyRightSchema>;

/**
 * Como o pedido aponta para a pessoa.
 *
 * Este objeto é ENTRADA, nunca saída: ele carrega dado pessoal e por isso não
 * aparece em nenhum DTO de resposta deste arquivo. No banco ele vira HMAC de
 * finalidade própria mais valor cifrado.
 */
export const PrivacySelectorSchema = z.discriminatedUnion('type', [
  z.strictObject({type: z.literal('conversation_id'), value: UuidV4Schema}),
  z.strictObject({
    type: z.literal('instagram_pseudonym'),
    value: MapaPseudonymSchema,
  }),
  z.strictObject({type: z.literal('whatsapp_phone'), value: PhoneE164Schema}),
]);
export type PrivacySelector = z.infer<typeof PrivacySelectorSchema>;

export const CreatePrivacyRequestSchema = z.strictObject({
  right: PrivacyRightSchema,
  selector: PrivacySelectorSchema,
  receivedVia: z.enum(['instagram', 'whatsapp']),
  receivedAt: IsoUtcDateTimeSchema,
});
export type CreatePrivacyRequest = z.infer<typeof CreatePrivacyRequestSchema>;

export const PrivacyRequestStatusSchema = z.enum([
  'received',
  'verification_pending',
  'verified',
  'executing',
  'delivery_ready',
  'completed',
  'rejected',
]);
export type PrivacyRequestStatus = z.infer<typeof PrivacyRequestStatusSchema>;

export const PrivacyRequestSchema = z.strictObject({
  id: UuidV4Schema,
  right: PrivacyRightSchema,
  status: PrivacyRequestStatusSchema,
  verificationChannel: z.enum(['instagram', 'whatsapp']).nullable(),
  /** Quando o TITULAR provou ser ele. Passkey de Thiago não preenche isto. */
  subjectVerifiedAt: IsoUtcDateTimeSchema.nullable(),
  dueAt: IsoUtcDateTimeSchema,
  completedAt: IsoUtcDateTimeSchema.nullable(),
});
export type PrivacyRequestDto = z.infer<typeof PrivacyRequestSchema>;

export const DELETION_TARGETS = [
  'postgres',
  'object_storage',
  'search_index',
  'application_logs',
  'n8n_execution_data',
] as const;
export const DeletionTargetSchema = z.enum(DELETION_TARGETS);
export type DeletionTarget = z.infer<typeof DeletionTargetSchema>;

export const DeletionPropagationStateSchema = z.enum([
  'pending',
  'completed',
  /** Provado que não se aplica. Diferente de "não olhei", que é `pending`. */
  'not_applicable_verified',
  'failed',
]);
export type DeletionPropagationState = z.infer<
  typeof DeletionPropagationStateSchema
>;

export const PrivacyRequestDetailSchema = PrivacyRequestSchema.extend({
  /** Referência opaca da operação. É o que liga o pedido à auditoria. */
  operationAuditRef: UuidV4Schema,
  propagations: z.array(
    z.strictObject({
      target: DeletionTargetSchema,
      state: DeletionPropagationStateSchema,
      evidenceHash: z
        .string()
        .regex(/^[0-9a-f]{64}$/u)
        .nullable(),
      updatedAt: IsoUtcDateTimeSchema,
    }),
  ),
  delivery: z
    .strictObject({
      state: z.enum(['none', 'ready', 'consumed', 'expired']),
      expiresAt: IsoUtcDateTimeSchema.nullable(),
    })
    .nullable(),
});
export type PrivacyRequestDetailDto = z.infer<
  typeof PrivacyRequestDetailSchema
>;

export const VerifyPrivacyRequestSchema = z.strictObject({
  challengeId: UuidV4Schema,
  /**
   * Código que o titular mandou de volta pelo canal dele. Vem no corpo, nunca
   * na URL: query entra em log de servidor e em histórico de navegador.
   */
  verificationCode: AttributionCodeSchema,
  /** Evento apagável que prova a volta pelo canal. Sem FK na auditoria. */
  evidenceEventId: UuidV4Schema,
});
export type VerifyPrivacyRequest = z.infer<typeof VerifyPrivacyRequestSchema>;

// ---------------------------------------------------------------------------
// Privacidade: execução e lápide
// ---------------------------------------------------------------------------

export const DeletionTombstoneSchema = z.strictObject({
  id: UuidV4Schema,
  requestId: UuidV4Schema,
  state: z.enum(['pending', 'propagating', 'completed', 'failed']),
  dueAt: IsoUtcDateTimeSchema,
  ledgerState: z.enum(['pending', 'written', 'failed']),
  completedAt: IsoUtcDateTimeSchema.nullable(),
});
export type DeletionTombstoneDto = z.infer<typeof DeletionTombstoneSchema>;

export const PrivacyResolutionRequestSchema = z.discriminatedUnion('action', [
  z.strictObject({action: z.literal('access')}),
  z.strictObject({
    action: z.literal('correction'),
    corrections: z.array(AnswerValueSchema).min(1).max(5),
  }),
  z.strictObject({action: z.literal('opposition')}),
  z.strictObject({
    action: z.literal('consent_revocation'),
    /** Finalidade fechada: revogar "tudo" não é um pedido interpretável. */
    purpose: z.literal('instagram_whatsapp_link'),
  }),
  z.strictObject({action: z.literal('erasure')}),
]);
export type PrivacyResolutionRequest = z.infer<
  typeof PrivacyResolutionRequestSchema
>;

export const PrivacyResolutionResponseSchema = z.discriminatedUnion('action', [
  z.strictObject({
    action: z.literal('access'),
    request: PrivacyRequestSchema,
    deliveryUrl: z.string().min(1),
    /**
     * Mostrado UMA vez para Thiago repassar ao titular pelo canal já
     * verificado. Não é dado pessoal do titular, é segredo de entrega, e por
     * isso não pode ser relido depois nem entrar em log.
     */
    deliveryCode: AttributionCodeSchema,
    expiresAt: IsoUtcDateTimeSchema,
  }),
  z.strictObject({
    action: z.literal('correction'),
    request: PrivacyRequestSchema,
  }),
  z.strictObject({
    action: z.literal('opposition'),
    request: PrivacyRequestSchema,
  }),
  z.strictObject({
    action: z.literal('consent_revocation'),
    request: PrivacyRequestSchema,
  }),
  z.strictObject({
    action: z.literal('erasure'),
    request: PrivacyRequestSchema,
    tombstone: DeletionTombstoneSchema,
  }),
]);
export type PrivacyResolutionResponse = z.infer<
  typeof PrivacyResolutionResponseSchema
>;

/**
 * Erro único do resgate.
 *
 * Código inexistente, já usado, vencido, revogado, de outra conversa ou sem
 * consentimento devolvem TODOS este mesmo código. Diferenciar contaria ao
 * atacante em qual das seis situações ele está, e é assim que se enumera
 * conversa alheia com tentativa e erro.
 */
export const CODE_NOT_REDEEMABLE = 'CODE_NOT_REDEEMABLE' as const;
